/**
 * LearningObjectiveCard - Educational Scaffolding Component
 * Minimal black/grey design matching micro-dashboard aesthetic
 */

import React from 'react';
import { Narrative } from '@/types/micro';
import { Lightbulb, Target, Zap } from 'lucide-react';

interface LearningObjectiveCardProps {
  sessionId: string;
  sessionTitle: string;
  skillArea: string;
  narrative?: Narrative;
  narrativeBridge?: string;
}

export const LearningObjectiveCard: React.FC<LearningObjectiveCardProps> = ({
  narrative,
  narrativeBridge
}) => {
  if (!narrative) return null;

  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-6 mb-6 shadow-sm">
      {narrativeBridge && (
        <div className="rounded-2xl border border-slate-100 bg-slate-50 p-5 mb-4">
          <p className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold mb-2">
            Why this lesson matters
          </p>
          <div className="text-sm text-slate-700 leading-relaxed space-y-3">
            {narrativeBridge.includes('\n\n') ? (
              // Respect explicit line breaks if present
              narrativeBridge.split('\n\n').map((para, i) => (
                <p key={i}>{para}</p>
              ))
            ) : (() => {
              // Smart split for continuous text
              const sentences = narrativeBridge.match(/[^.!?]+[.!?]+(\s+|$)/g);
              if (!sentences || sentences.length < 2) {
                return <p>{narrativeBridge}</p>;
              }

              // Find the middle sentence to split at
              const midpoint = Math.ceil(sentences.length / 2);
              const firstPara = sentences.slice(0, midpoint).join('').trim();
              const secondPara = sentences.slice(midpoint).join('').trim();

              return (
                <>
                  <p>{firstPara}</p>
                  <p>{secondPara}</p>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Learning Objectives - Minimal Style */}
      <div className="space-y-3">
        {/* What */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-start gap-3">
            <Lightbulb className="w-4 h-4 text-slate-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">
                What You'll Do
              </p>
              <p className="text-sm text-slate-700 leading-relaxed">
                {narrative.what}
              </p>
            </div>
          </div>
        </div>



        {/* How */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-start gap-3">
            <Zap className="w-4 h-4 text-slate-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">
                How It Works
              </p>
              <p className="text-sm text-slate-700 leading-relaxed">
                {narrative.how}
              </p>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};
